import { Check, Database, Flag, Lock, RefreshCw, RotateCcw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  DataTable,
  type DataTableColumn,
} from "@/components/cinch/DataTable/DataTable";
import { KpiTile } from "@/components/cinch/KpiTile";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useBillingStart,
  usePullBillingStart,
  useReviewBillingStartItem,
  type BillingStartItem,
  type ReviewStatus,
} from "../api";
import { fmtCur, fmtDate, relativeAgo } from "../../renewals/format";

/** R-02 worklists in two tabs:
 *  1. First-time billing — accounts whose FIRST billed month is the close
 *     month, default-filtered to rows where the contract's billing effective
 *     date doesn't align (or the actual first invoice date disagrees).
 *     Approve / flag each row; flags block completing the step.
 *  2. Missing billing effective — contracts with no billing effective date
 *     that have never billed a dollar. */
export function BillingStartPanel({ period }: { period: string }) {
  const { data, isLoading, error } = useBillingStart(period);
  const pull = usePullBillingStart(period);
  const [tab, setTab] = useState<"first" | "missing">("first");
  const [misalignedOnly, setMisalignedOnly] = useState(true);
  const [flagTarget, setFlagTarget] = useState<BillingStartItem | null>(null);

  const items = useMemo(() => data?.items ?? [], [data]);
  const firstBilled = useMemo(
    () => items.filter((i) => i.kind === "first_billed"),
    [items],
  );
  const missing = useMemo(
    () => items.filter((i) => i.kind === "missing_effective"),
    [items],
  );
  const firstRows = useMemo(
    () =>
      misalignedOnly
        ? firstBilled.filter(
            (i) => !i.payload.aligned || i.payload.invoice_discrepancy,
          )
        : firstBilled,
    [firstBilled, misalignedOnly],
  );

  const canAct = (data?.can_act ?? false) && !(data?.locked ?? false);
  const pulled = !!data?.pulled_at;
  const s = data?.summary;

  function doPull() {
    pull.mutate(undefined, {
      onSuccess: (p) =>
        toast.success(
          `Pulled billing-start worklists — ${p.summary?.first_billed_count ?? 0} first-time billers, ${p.summary?.missing_effective_count ?? 0} missing billing effective`,
        ),
      onError: (e) => toast.error("Pull failed", { description: (e as Error).message }),
    });
  }

  if (isLoading && !data) return <Card className="h-64 animate-pulse" />;

  if (!isLoading && !pulled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step data</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-3 rounded-md border border-dashed bg-muted/30 px-4 py-10 text-center">
            <Database className="h-8 w-8 text-muted-foreground" />
            <p className="max-w-md text-sm text-muted-foreground">
              {monthLabel(period)}'s billing-start worklists haven't been pulled
              yet: first-time billers checked against their contract's billing
              effective date, plus contracts missing one entirely.
            </p>
            {canAct ? (
              <Button onClick={doPull} disabled={pull.isPending}>
                <RefreshCw
                  className={cn("mr-1.5 h-4 w-4", pull.isPending && "animate-spin")}
                />
                {pull.isPending ? "Pulling…" : "Pull from Snowflake"}
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                An R-02 owner (RevOps / CS) runs the pull.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label={`First-time billers · ${monthLabel(period)}`}
          value={String(s?.first_billed_count ?? 0)}
        />
        <KpiTile
          label="Billing effective misaligned"
          value={String(s?.misaligned_count ?? 0)}
          tone={(s?.misaligned_count ?? 0) > 0 ? "warning" : "positive"}
          sub={`${s?.invoice_discrepancy_count ?? 0} first-invoice discrepancies`}
        />
        <KpiTile
          label="Missing billing effective"
          value={String(s?.missing_effective_count ?? 0)}
          tone={(s?.missing_effective_count ?? 0) > 0 ? "warning" : "positive"}
          sub="never billed, no start date"
          onClick={() => setTab("missing")}
          selected={tab === "missing"}
        />
        <KpiTile
          label="Flags"
          value={String(s?.flagged_count ?? 0)}
          tone={(s?.flagged_count ?? 0) > 0 ? "danger" : "positive"}
          sub={
            (s?.flagged_count ?? 0) > 0
              ? "must be cleared before R-02 completes"
              : `${s?.approved_count ?? 0} approved · ${s?.pending_count ?? 0} pending`
          }
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1 border-b border-border">
          {(
            [
              ["first", `First-time billing (${firstBilled.length})`],
              ["missing", `Missing billing effective (${missing.length})`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={cn(
                "rounded-t-md border-b-2 px-3 py-1.5 text-sm font-medium",
                tab === key
                  ? "border-brand text-brand"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data?.locked && (
            <span className="inline-flex items-center gap-1 font-medium text-success">
              <Lock className="h-3.5 w-3.5" /> Signed off — frozen
            </span>
          )}
          {data?.pulled_at && (
            <span title={new Date(data.pulled_at).toLocaleString()}>
              Pulled {relativeAgo(data.pulled_at)}
              {data.pulled_by_name ? ` by ${data.pulled_by_name}` : ""}
            </span>
          )}
          {canAct && (
            <Button variant="outline" size="sm" onClick={doPull} disabled={pull.isPending}>
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", pull.isPending && "animate-spin")}
              />
              {pull.isPending ? "Pulling…" : "Re-pull"}
            </Button>
          )}
        </div>
      </div>

      {tab === "first" ? (
        <>
          <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
            <Switch checked={misalignedOnly} onCheckedChange={setMisalignedOnly} />
            Only rows that don't line up ({firstRows.length} of {firstBilled.length})
          </label>
          <FirstBilledTable
            period={period}
            rows={firstRows}
            canAct={canAct}
            error={error ? String(error) : null}
            onFlag={setFlagTarget}
          />
        </>
      ) : (
        <MissingEffectiveTable rows={missing} error={error ? String(error) : null} />
      )}

      <FlagDialog
        period={period}
        item={flagTarget}
        onClose={() => setFlagTarget(null)}
      />
    </div>
  );
}

// ── Tab 1: first-time billing alignment ─────────────────────────────────────

function FirstBilledTable({
  period,
  rows,
  canAct,
  error,
  onFlag,
}: {
  period: string;
  rows: BillingStartItem[];
  canAct: boolean;
  error: string | null;
  onFlag: (item: BillingStartItem) => void;
}) {
  const review = useReviewBillingStartItem(period);

  const columns: DataTableColumn<BillingStartItem>[] = [
    {
      key: "qb_id",
      header: "QB ID",
      className: "w-20",
      render: (i) => (
        <span className="font-mono text-xs text-muted-foreground">
          {i.payload.qb_id ?? "—"}
        </span>
      ),
    },
    {
      key: "account",
      header: "Account",
      className: "max-w-[240px]",
      render: (i) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {i.payload.cinch_account_name ?? i.payload.qb_customer_name ?? "—"}
          </span>
          {!i.payload.cinch_account_name && (
            <span className="text-[11px] text-warning">no Cinch account</span>
          )}
        </div>
      ),
    },
    {
      key: "contract_type_name",
      header: "Contract",
      render: (i) =>
        i.payload.contract_id ? (
          <span className="text-sm">{i.payload.contract_type_name ?? "—"}</span>
        ) : (
          <span className="font-medium text-warning">no contract found</span>
        ),
    },
    {
      key: "contract_signed_at",
      header: "Signed",
      render: (i) => fmtDate(i.payload.contract_signed_at),
    },
    {
      key: "billing_effective_from",
      header: "Billing Effective",
      render: (i) =>
        i.payload.billing_effective_from ? (
          <span
            className={cn(
              "tabular-nums",
              !i.payload.aligned && "font-semibold text-warning",
            )}
            title={
              i.payload.aligned
                ? undefined
                : `Not in ${monthLabel(period)} — the account first billed this month`
            }
          >
            {fmtDate(i.payload.billing_effective_from)}
          </span>
        ) : (
          <span className="font-semibold text-warning">missing</span>
        ),
    },
    {
      key: "first_invoice_date",
      header: "First Invoice",
      render: (i) => (
        <span
          className={cn(
            "tabular-nums",
            i.payload.invoice_discrepancy && "font-semibold text-warning",
          )}
          title={
            i.payload.invoice_discrepancy
              ? "Doesn't match the billing effective date"
              : undefined
          }
        >
          {fmtDate(i.payload.first_invoice_date)}
        </span>
      ),
    },
    {
      key: "mrr_total",
      header: "Contract MRR",
      numeric: true,
      render: (i) => fmtCur(i.payload.mrr_total),
    },
    {
      key: "review",
      header: "Review",
      align: "right",
      className: "w-44",
      render: (i) => (
        <span className="inline-flex items-center justify-end gap-1.5">
          <StatusBadge value={i.review_status} />
          {canAct && (
            <>
              <ReviewButton
                title="Approve — start date is as expected"
                active={i.review_status === "approved"}
                activeClass="text-success"
                hoverClass="hover:bg-success-light hover:text-success"
                onClick={() =>
                  review.mutate(
                    { itemKey: i.item_key, review: { status: "approved" } },
                    {
                      onError: (e) =>
                        toast.error("Couldn't approve", {
                          description: (e as Error).message,
                        }),
                    },
                  )
                }
              >
                <Check className="h-3.5 w-3.5" />
              </ReviewButton>
              <ReviewButton
                title="Flag — something's off (add notes)"
                active={i.review_status === "flagged"}
                activeClass="text-warning"
                hoverClass="hover:bg-warning-light hover:text-warning"
                onClick={() => onFlag(i)}
              >
                <Flag className="h-3.5 w-3.5" />
              </ReviewButton>
            </>
          )}
        </span>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(i) => i.item_key}
      error={error}
      emptyMessage="Nothing to review — every first-time biller lines up."
      containerClassName=""
      renderExpanded={(i) => <ItemDetail item={i} />}
    />
  );
}

// ── Tab 2: contracts missing a billing effective date ───────────────────────

function MissingEffectiveTable({
  rows,
  error,
}: {
  rows: BillingStartItem[];
  error: string | null;
}) {
  const columns: DataTableColumn<BillingStartItem>[] = [
    {
      key: "company",
      header: "Company",
      className: "max-w-[280px]",
      render: (i) => (
        <div className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {i.payload.company_name ?? i.payload.cinch_account_name ?? "—"}
          </span>
          {i.payload.company_name_dba && (
            <span className="block truncate text-xs text-muted-foreground">
              dba {i.payload.company_name_dba}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "contract_type_name",
      header: "Contract Type",
      render: (i) => i.payload.contract_type_name ?? "—",
    },
    {
      key: "contract_signed_at",
      header: "Signed",
      render: (i) => fmtDate(i.payload.contract_signed_at),
    },
    {
      key: "mrr_total",
      header: "MRR",
      numeric: true,
      render: (i) => fmtCur(i.payload.mrr_total),
    },
    {
      key: "term_months",
      header: "Term",
      render: (i) =>
        i.payload.term_months ? `${i.payload.term_months} months` : "—",
    },
    {
      key: "closer_name",
      header: "Closer",
      render: (i) => i.payload.closer_name ?? "—",
    },
    {
      key: "qb_id",
      header: "QB ID",
      align: "right",
      render: (i) => (
        <span className="font-mono text-xs text-muted-foreground">
          {i.payload.qb_id ?? "—"}
        </span>
      ),
    },
  ];
  return (
    <DataTable
      columns={columns}
      rows={rows}
      rowKey={(i) => i.item_key}
      error={error}
      emptyMessage="Every never-billed contract has a billing effective date."
      containerClassName=""
    />
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────

function ItemDetail({ item }: { item: BillingStartItem }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-2 py-1 text-xs sm:grid-cols-4">
      <Fact label="QB customer">{item.payload.qb_customer_name ?? "—"}</Fact>
      <Fact label="Contract id">
        <span className="font-mono">{item.payload.contract_id ?? "—"}</span>
      </Fact>
      <Fact label="First billed month">
        {item.payload.first_month ? monthLabel(item.payload.first_month.slice(0, 7)) : "—"}
      </Fact>
      <Fact label="Reviewed">
        {item.reviewed_at
          ? `${item.reviewed_by_name ?? "—"} · ${new Date(item.reviewed_at).toLocaleString()}`
          : "not yet"}
      </Fact>
      {item.review_notes && (
        <Fact label="Notes" className="col-span-full">
          {item.review_notes}
        </Fact>
      )}
    </dl>
  );
}

function Fact({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function ReviewButton({
  title,
  active,
  activeClass,
  hoverClass,
  onClick,
  children,
}: {
  title: string;
  active: boolean;
  activeClass: string;
  hoverClass: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      className={cn(
        "flex h-6 w-6 items-center justify-center rounded",
        hoverClass,
        active ? activeClass : "text-muted-foreground",
      )}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

function FlagDialog({
  period,
  item,
  onClose,
}: {
  period: string;
  item: BillingStartItem | null;
  onClose: () => void;
}) {
  const review = useReviewBillingStartItem(period);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<ReviewStatus>("flagged");

  // Seed when a new item opens.
  const seeded = useState<string | null>(null);
  if (item && seeded[0] !== item.item_key) {
    seeded[1](item.item_key);
    setNotes(item.review_notes ?? "");
    setStatus("flagged");
  }

  const name =
    item?.payload.cinch_account_name ??
    item?.payload.qb_customer_name ??
    item?.payload.company_name ??
    "";

  return (
    <Dialog open={item != null} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Review {name}</DialogTitle>
          <DialogDescription>
            Flagged rows block completing this step until they're approved or
            cleared. Say what looks off so whoever fixes it has context.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["flagged", Flag, "Flag"],
              ["approved", Check, "Approve"],
              ["pending", RotateCcw, "Clear"],
            ] as const
          ).map(([value, Icon, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setStatus(value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium",
                status === value
                  ? value === "flagged"
                    ? "border-warning bg-warning-light text-warning"
                    : value === "approved"
                      ? "border-success bg-success-light text-success"
                      : "border-border bg-muted text-foreground"
                  : "border-border text-muted-foreground hover:bg-muted",
              )}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          aria-label="Review notes"
          placeholder="What looks off about this billing start?"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!item) return;
              review.mutate(
                { itemKey: item.item_key, review: { status, notes } },
                {
                  onSuccess: () => {
                    toast.success(`${name} ${status === "pending" ? "cleared" : status}`);
                    onClose();
                  },
                  onError: (e) =>
                    toast.error("Couldn't save review", {
                      description: (e as Error).message,
                    }),
                },
              );
            }}
            disabled={review.isPending}
          >
            {review.isPending ? "Saving…" : "Save review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
